import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeCardModule, ForgeCircularProgressModule } from "@tylertech/forge-angular";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { from, Observable, of, Subject, throwError } from "rxjs";
import { catchError, map, switchMap, takeUntil } from "rxjs/operators";
import { OpenIdService, RouteInfoService, SecurityService, SystemInfoExtended, SystemInfoService } from "../../services";
import { ChangePasswordFormComponent } from "./changePasswordForm/changePasswordForm.component";
import { ForgotPasswordFormComponent } from "./forgotPasswordForm/forgotPasswordForm.component";
import { LoginFormComponent } from "./loginForm/loginForm.component";
import { MigrateTidFormComponent } from "./migrateTidForm/migrateTidForm.component";
import { ResetPasswordFormComponent } from "./resetPasswordForm/resetPasswordForm.component";
import { SecurityScreenType } from "./securityScreenType";
import { SignoutFormComponent } from "./signoutForm/signoutForm.component";

const enum ProductLogoUrl
{
    ErpPro9 = "/library/tylerui/images/erp_pro_9.jpg",
    ErpPro10 = "/library/tylerui/images/erp_pro_10.jpg",
    ErpProSchools = "/library/tylerui/images/school_erp_pro.jpg",
}

interface SecurityScreenData
{
    readonly formType: SecurityScreenType,
    readonly bypassTylerId?: boolean;
}

@Component({
    selector: "security-screen",
    templateUrl: "./securityScreen.tmpl.html",
    styleUrls: [
        "./securityScreen.component.scss"
    ],
    imports: [
        ChangePasswordFormComponent,
        FlexDirective,
        ForgeCardModule,
        ForgeCircularProgressModule,
        ForgotPasswordFormComponent,
        LayoutAlignDirective,
        LayoutDirective,
        LoginFormComponent,
        MigrateTidFormComponent,
        ResetPasswordFormComponent,
        SignoutFormComponent,
    ]
})
export class SecurityScreenComponent implements OnInit
{
    private readonly $securityService = inject(SecurityService);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $routeInfoService = inject(RouteInfoService);
    private readonly $openIdService = inject(OpenIdService);


    private readonly destructor$ = new Subject<void>();
    private readonly log = getLoggerFor("SecurityScreenComponent");

    private _useTylerId: boolean;
    protected routeParams: Record<string, string>;
    protected queryParams: Record<string, string>;

    error: boolean;
    logo: ProductLogoUrl;
    organization: string;
    version: string;
    hidden: boolean;
    authenticatingTid: boolean = false;
    productText: string;
    isDisabled: boolean;
    busy: boolean;
    showForgotPwLink: boolean;
    showRememberMe: boolean;
    bypassTylerId: boolean;
    formType: SecurityScreenType;

    constructor()
    {
        const activatedRoute = inject(ActivatedRoute);

        activatedRoute.queryParamMap
            .pipe(takeUntil(this.destructor$))
            .subscribe((paramMap: ParamMap) =>
            {
                this.queryParams = paramMap.keys.reduce(
                    (obj: Record<string, string>, key: string) =>
                    {
                        obj[key] = paramMap.get(key);

                        return obj;
                    },
                    {});
            });

        activatedRoute.paramMap
            .pipe(takeUntil(this.destructor$))
            .subscribe((paramMap: ParamMap) =>
            {
                this.routeParams = paramMap.keys.reduce(
                    (obj: Record<string, string>, key: string) =>
                    {
                        obj[key] = paramMap.get(key);

                        return obj;
                    },
                    {});
            });

        activatedRoute.data
            .pipe(takeUntil(this.destructor$))
            .subscribe((data: SecurityScreenData) =>
            {
                this.formType = data.formType;
                this.bypassTylerId = data.bypassTylerId;
            });
    }

    get useTylerId(): boolean
    {
        if (this.bypassTylerId)
        {
            return false;
        }
        return this._useTylerId;
    }

    set useTylerId(value: boolean)
    {
        this._useTylerId = value;
    }

    get tenant(): string
    {
        return this.$routeInfoService.tenant?.replace(/"/g, "");
    }

    get SubHeader(): string
    {
        switch (this.formType)
        {
            case SecurityScreenType.ChangePassword:
                return this.$securityService.Username.replace(/"/g, "");
            case SecurityScreenType.Login:
            case SecurityScreenType.ForgotPassword:
            case SecurityScreenType.ResetPassword:
                return this.organization;
        }
        return "";
    }

    get IsLogin(): boolean
    {
        return !this.useTylerId && this.formType === SecurityScreenType.Login;
    }

    get IsSignout(): boolean
    {
        return this.formType === SecurityScreenType.Signout;
    }

    get IsChangePassword(): boolean
    {
        return !this.useTylerId && this.formType === SecurityScreenType.ChangePassword;
    }

    get IsForgotPassword(): boolean
    {
        return !this.useTylerId && this.formType === SecurityScreenType.ForgotPassword;
    }

    get IsResetPassword(): boolean
    {
        return !this.useTylerId && this.formType === SecurityScreenType.ResetPassword;
    }

    get IsMigrateTidUser(): boolean
    {
        return this.formType === SecurityScreenType.MigrateTidUser;
    }

    ngOnInit(): void
    {
        this.initialize()
            .subscribe({
                error: (e) =>
                {
                    this.log.error(`ngOnInit ERROR => ${JSON.stringify(e)}`);
                },
                complete: () =>
                {
                    this.busy = false;
                }
            });
    }

    /**
     * This method was extracted from ngOnInit so that we can capture the async processes in tests and wait completion
     * before running any expect calls.
     */
    protected initialize(): Observable<void>
    {
        //handle auth code flow callback
        if (this.queryParams.code && this.queryParams.state)
        {
            this.authenticatingTid = true;
            this.hidden = true;
            this.useTylerId = true;
            // TODO - why is this component responding to the TylerId return.
            return from(this.$openIdService.getUser())
                .pipe(
                    switchMap(user =>
                    {
                        if (user)
                        {
                            return this.handleTylerId()
                                .pipe(
                                    map(() =>
                                    {
                                        this.hidden = false;
                                        this.authenticatingTid = false;
                                    }));
                        }

                        return of();
                    }),
                    catchError((_, _caught) =>
                    {
                        //do nothing, oidc client handles error message so we want to suppress the console error
                        return of();
                    })
                );
        }

        this.busy = true;

        this.log.debug(`ngOnInit - begin loading`);
        // TODO(DO-4549) - we can probably avoid the Observable here and just rely on SystemInfoService.systemInfo since we ensure the SystemInfo is updated during the APP_INITIALIZER function.
        return this.$systemInfoService.getSystemInfo()
            .pipe(
                map(systemInfo => this.setState(systemInfo)),
                catchError(_e =>
                {
                    this.version = "Unable to reach service, please try again later.";
                    this.isDisabled = true;
                    this.showForgotPwLink = false;
                    this.showRememberMe = false;

                    return of(false);
                }),
                switchMap((success: boolean) =>
                {
                    if (success
                        && !this.IsMigrateTidUser
                        && !this.IsSignout
                        && !this.bypassTylerId
                        && this.$systemInfoService.systemInfo.useTylerId
                        && !this.queryParams?.K)
                    {
                        // TODO - should this live somewhere else that is not a component. Either the SecurityService, or the TylerIdLoginStrategy
                        this.useTylerId = true;

                        return this.handleTylerId();
                    }

                    return of();
                }));
    }

    @LogInvocation(LogLevel.DEBUG)
    protected setState(systemInfo: SystemInfoExtended): boolean
    {
        this.version = systemInfo.DisplayVersion;
        this.organization = systemInfo.Organization;

        if (systemInfo.IsIncode9())
        {
            this.showForgotPwLink = false;
            this.showRememberMe = false;
            this.productText = "ERP Pro 9";
        }
        else if (systemInfo.IsIncode10())
        {
            this.showForgotPwLink = true;
            this.showRememberMe = true;
            this.productText = "ERP Pro 10";
        }
        else if (systemInfo.IsSchoolERPPro()
            || /^iv/i.test(this.$routeInfoService.tenant))
        {
            this.showForgotPwLink = true;
            this.showRememberMe = true;
            this.productText = "School ERP Pro";
        }

        return true;
    }

    /**
     *  TODO - this should live somewhere that is not a component. Either the SecurityService, or the TylerIdLoginStrategy
     * @deprecated
     */
    @LogInvocation(LogLevel.DEBUG)
    protected handleTylerId(): Observable<void>
    {
        if (!this.$openIdService.getToken())
        {
            return from(this.$openIdService.login());
        }
        return this.$securityService.authenticateTylerId()
            .pipe(
                map(r =>
                {
                    this.log.debug(`$securityService.authenticateTylerId => ${JSON.stringify(r)}`);
                    this.authenticatingTid = false;
                }),
                catchError(e =>
                {
                    this.log.error(`$securityService.authenticateTylerId => ${JSON.stringify(e)}`);
                    this.authenticatingTid = false;
                    this.error = true;
                    this.hidden = false;

                    return throwError(() => new Error(`AuthenticationError: TylerId could not authenticate user; ${e.message}`));
                }));
    }

    @LogInvocation(LogLevel.DEBUG)
    onGoto($event: SecurityScreenType): void
    {
        this.formType = $event;
    }
}
